"use client";

import { Input } from "@/components/atoms/Input";
import CheckIcon from "@/icons/__generated__/Check/Check20";
import SearchIcon from "@/icons/__generated__/Search/Search20";
import { cn } from "@/lib/utils";
import {
  useCombobox,
  UseComboboxIsOpenChange,
  UseComboboxSelectedItemChange,
} from "downshift";
import Fuse from "fuse.js";
import * as React from "react";

export type ComboboxFilterProps = {
  selectedValue: string | undefined;
  options: { value: string; label: string }[];
  onSelectedItemChange?: (
    selectedItem: UseComboboxSelectedItemChange<{
      value: string;
      label: string;
    }>,
  ) => void;
  onIsOpenChange?: (
    changes: UseComboboxIsOpenChange<{ value: string; label: string }>,
  ) => void;
  placeholder?: string | undefined;
  emptyMessage?: string;
  keys?: string[];
  threshold?: number;
  testId?: string;
};

export const ComboboxFilter = ({
  placeholder = "Search...",
  options,
  onSelectedItemChange,
  onIsOpenChange,
  emptyMessage,
  selectedValue,
  testId = "combobox-filter",
  keys = ["label"], // Perform fuzzy search on the 'label' key
  threshold = 0.3, // Lower values mean stricter matches
}: ComboboxFilterProps) => {
  const [filteredOptions, setFilteredOptions] = React.useState(options);

  // Initialize Fuse with options and specify the search keys
  const fuse = React.useMemo(
    () =>
      new Fuse(options, {
        keys,
        threshold,
      }),
    [options, keys, threshold],
  );

  const {
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    setInputValue,
    selectItem,
  } = useCombobox({
    items: filteredOptions,
    itemToString: (item) => (item ? item.label : ""),
    onSelectedItemChange,
    onIsOpenChange,
    onInputValueChange: ({ inputValue }) => {
      // Perform fuzzy search if there's input, otherwise show all options
      const results = inputValue
        ? fuse.search(inputValue).map((result) => result.item)
        : options;
      setFilteredOptions(results);
    },
  });

  return (
    <div>
      <Input
        prefix={<SearchIcon />}
        fullWidth={true}
        testId={testId ? `${testId}-input` : undefined}
        placeholder={placeholder}
        className="tw-border-0 tw-border-b tw-border-b-gray-900 focus:tw-outline-0 tw-rounded-bl-0 tw-rounded-br-0"
        onChange={(e) => setInputValue(e.target.value)}
        {...getInputProps()}
      />
      <ul
        {...getMenuProps()}
        className="tw-overflow-y-auto tw-bg-base-white tw-rounded-4 tw-rounded-tl-0 tw-rounded-tr-0"
        style={{ maxHeight: "240px" }}
      >
        {!filteredOptions?.length ? (
          <li className="tw-p-8 tw-text-gray-500">
            {emptyMessage || "No options found."}
          </li>
        ) : (
          (filteredOptions || []).map((option, index) => {
            return (
              <li
                key={option.value}
                {...getItemProps({
                  index,
                  item: option,
                  className: cn(
                    "tw-flex tw-items-center tw-p-8 tw-gap-8 tw-cursor-pointer",
                    {
                      "tw-bg-gray-300": highlightedIndex === index,
                    },
                  ),
                  onClick: () => selectItem(option),
                })}
              >
                <CheckIcon
                  className={cn(
                    selectedValue === option.value
                      ? "tw-opacity-100"
                      : "tw-opacity-0",
                  )}
                />
                {option.label}
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
};
